import * as THREE from 'three'
import { mergeBodyEntriesById } from '../body-entries'
import { resolveImportedDisplayName } from '../original-node-name'
import { asRecord } from '../utils'
import { buildImportedPartBodyBrepBindings } from '../build-imported-part-body-brep-bindings'
import {
  enrichBodyEntriesWithModelBodyIdentities,
} from '../enrich-body-entry-identities'
import { readImportedTopologyFaceTriangleRangeBindings } from '../imported-topology-face-range-binding'
import {
  resolveImportedMeshBrepBindingPlanContext,
  resolveImportedPrimitiveBrepBindingPlan,
} from '../imported-brep-binding-plan'
import { cloneImportedModelUnitContext } from '../imported-model-unit-context'
import {
  computeImportedMeshPrimitiveLocalBounds,
  mergeImportedTopologyBounds,
} from '../../topology/imported-model-topology-bounds'
import {
  resolveNodeRecords,
  resolveNodeSemanticKind,
} from '../assembly-node-utils'
import {
  rebaseImportedBodyOccurrenceBindingId,
  rebaseImportedTopologySourceOccurrence,
} from '../../utils'
import type { ImportedBodyEntry } from '../glb'
import type {
  ImportedBrepRef,
  ImportedModelUnitContext,
  ImportedPartBodyPrimitiveDescriptor,
  ImportedPartMeshBrepBinding,
  ImportedTopologyBounds,
  ImportedTopologySource,
  LoaderMetadata,
  PartAsset,
  PartMeshMeta,
  PartPrimitiveMeta,
} from '../types'

/**
 * 克隆 loader plain metadata，避免拓扑源直接暴露调用方可变对象。
 */
function cloneMetadata(value: unknown): LoaderMetadata | undefined {
  const record = asRecord(value)
  return record ? { ...record } : undefined
}

/**
 * 判断对象是否是 assembly/part 语义 occurrence 根。
 */
function isSemanticOccurrence(object: THREE.Object3D): boolean {
  const semanticKind = resolveNodeSemanticKind(resolveNodeRecords(object))
  return semanticKind === 'assembly' || semanticKind === 'part'
}

/**
 * 收集拓扑源允许访问的对象集合。
 *
 * occurrence 模式从指定根开始，遇到下一层语义 occurrence 时停止递归；调用方额外传入
 * ownedMeshes 时，只保留这些 mesh 及其到 scope root 的祖先 metadata carrier。
 */
function collectTopologySourceObjects(
  root: THREE.Object3D,
  options: PuppyCAD.ImportedTopologySourceBuildOptions,
): readonly THREE.Object3D[] {
  const scopeRoot = options.occurrenceRoot ?? root
  const scopedObjects: THREE.Object3D[] = []

  const visit = (object: THREE.Object3D): void => {
    if (
      options.occurrenceRoot
      && object !== scopeRoot
      && isSemanticOccurrence(object)
    ) {
      return
    }
    scopedObjects.push(object)
    object.children.forEach(visit)
  }
  visit(scopeRoot)

  if (!options.ownedMeshes) {
    return scopedObjects
  }

  const scopedObjectSet = new Set(scopedObjects)
  const requiredObjects = new Set<THREE.Object3D>()
  for (const mesh of options.ownedMeshes) {
    if (!scopedObjectSet.has(mesh)) {
      continue
    }
    let current: THREE.Object3D | null = mesh
    while (current && scopedObjectSet.has(current)) {
      requiredObjects.add(current)
      if (current === scopeRoot) {
        break
      }
      current = current.parent
    }
  }

  return scopedObjects.filter((object) => requiredObjects.has(object))
}

/** 收集 body 对应的 GLTF primitive 下标。 */
function buildBodyPrimitiveIndicesById(
  objects: readonly THREE.Object3D[],
): Record<string, number[]> {
  const indexSetsByBodyId = new Map<string, Set<number>>()
  for (const object of objects) {
    const primitiveEntityEntries = Array.isArray(object.userData?.primitiveEntityEntries)
      ? object.userData.primitiveEntityEntries as Array<{ primitiveIndex?: unknown; bodyId?: unknown }>
      : []
    primitiveEntityEntries.forEach((entry) => {
      const bodyId = typeof entry?.bodyId === 'string' ? entry.bodyId.trim() : ''
      const primitiveIndex = typeof entry?.primitiveIndex === 'number' ? entry.primitiveIndex : NaN
      if (!bodyId || !Number.isInteger(primitiveIndex) || primitiveIndex < 0) {
        return
      }
      let indexSet = indexSetsByBodyId.get(bodyId)
      if (!indexSet) {
        indexSet = new Set<number>()
        indexSetsByBodyId.set(bodyId, indexSet)
      }
      indexSet.add(primitiveIndex)
    })
  }

  const result: Record<string, number[]> = {}
  indexSetsByBodyId.forEach((indexSet, bodyId) => {
    result[bodyId] = Array.from(indexSet).sort((a, b) => a - b)
  })
  return result
}

/** 读取 mesh 材质名称集合。 */
function resolveMaterialNames(mesh: THREE.Mesh): string[] {
  const materials = Array.isArray(mesh.material) ? mesh.material : [mesh.material]
  const names = new Set<string>()
  materials.forEach((material) => {
    const name = material?.name?.trim()
    if (name) {
      names.add(name)
    }
  })
  return Array.from(names)
}

/** 为 primitive 创建稳定的 source 内身份。 */
function buildPrimitiveId(
  meshId: string,
  primitiveIndex: number,
  primitiveExtras?: Record<string, unknown> | null,
): string {
  const explicitId = typeof primitiveExtras?.id === 'string' ? primitiveExtras.id.trim() : ''
  if (explicitId) {
    return explicitId
  }
  const stepEntityId = typeof primitiveExtras?.entityId === 'string'
    ? primitiveExtras.entityId.trim()
    : ''
  return stepEntityId
    ? `${meshId}:${stepEntityId}`
    : `${meshId}:primitive:${primitiveIndex}`
}

/** 将 primitive BRep 引用收口为唯一 mesh 引用。 */
function resolveMeshBrepRef(
  primitiveRefs: Array<ImportedBrepRef | undefined>,
): ImportedBrepRef | undefined {
  const uniqueRefs = primitiveRefs
    .filter((brepRef): brepRef is ImportedBrepRef => Boolean(brepRef))
    .reduce<ImportedBrepRef[]>((bucket, brepRef) => {
      const duplicated = bucket.some((candidate) => (
        candidate.uri === brepRef.uri
        && candidate.key === brepRef.key
        && candidate.sha256 === brepRef.sha256
      ))
      if (!duplicated) {
        bucket.push(brepRef)
      }
      return bucket
    }, [])
  return uniqueRefs.length === 1 ? uniqueRefs[0] : undefined
}

/** 将 primitive bodyId 收口为唯一 mesh bodyId。 */
function resolveMeshBodyId(bodyIds: Array<string | undefined>): string | undefined {
  const uniqueBodyIds = Array.from(new Set(
    bodyIds
      .filter((bodyId): bodyId is string => typeof bodyId === 'string' && bodyId.trim().length > 0)
      .map((bodyId) => bodyId.trim()),
  ))
  return uniqueBodyIds.length === 1 ? uniqueBodyIds[0] : undefined
}

/** 计算一个几何组承载的三角形数量。 */
function resolveTriangleCount(
  mesh: THREE.Mesh,
  group?: THREE.BufferGeometry['groups'][number],
): number {
  if (group) {
    return Math.max(0, Math.floor(group.count / 3))
  }
  const indexCount = mesh.geometry?.index?.count ?? 0
  if (indexCount > 0) {
    return Math.max(0, Math.floor(indexCount / 3))
  }
  const positionCount = mesh.geometry?.getAttribute('position')?.count ?? 0
  return Math.max(0, Math.floor(positionCount / 3))
}

/** 从 occurrence 已拥有的 mesh 构建 primitive、mesh 和 BRep catalog。 */
function buildPartCatalogs(meshes: readonly THREE.Mesh[]): {
  primitiveCatalog: Record<string, PartPrimitiveMeta>
  meshCatalog: Record<string, PartMeshMeta>
  brepBindingsByMeshId: Record<string, ImportedPartMeshBrepBinding>
  bodyBrepBindings: NonNullable<PartAsset['bodyBrepBindings']>
  bodyBrepBindingIdByPrimitiveId: NonNullable<PartAsset['bodyBrepBindingIdByPrimitiveId']>
  visibleBoundsByBodyBindingId: NonNullable<PartAsset['visibleBoundsByBodyBindingId']>
} {
  const primitiveCatalog: Record<string, PartPrimitiveMeta> = {}
  const meshCatalog: Record<string, PartMeshMeta> = {}
  const brepBindingsByMeshId: Record<string, ImportedPartMeshBrepBinding> = {}
  const bodyBrepBindings: NonNullable<PartAsset['bodyBrepBindings']> = {}
  const bodyBrepBindingIdByPrimitiveId: NonNullable<PartAsset['bodyBrepBindingIdByPrimitiveId']> = {}
  const visibleBoundsByBodyBindingId: NonNullable<PartAsset['visibleBoundsByBodyBindingId']> = {}
  let meshCounter = 0

  for (const object of meshes) {
    meshCounter += 1
    const meshId = `mesh:${meshCounter}`
    const geometryGroups = object.geometry?.groups ?? []
    const primitiveIds: string[] = []
    const primitiveFaceRanges: Array<{
      primitiveId: string
      startFaceIndex: number
      endFaceIndex: number
    }> = []
    const faceTriangleRanges: Array<[number, number]> = []
    const primitiveCount = geometryGroups.length > 0 ? geometryGroups.length : 1
    const primitiveBrepRefs: Array<ImportedBrepRef | undefined> = []
    const primitiveBodyIds: Array<string | undefined> = []
    const primitiveDescriptors: ImportedPartBodyPrimitiveDescriptor[] = []
    const primitiveVisibleBoundsById: Record<string, ImportedTopologyBounds> = {}
    let nextFaceStart = 0

    type PrimitiveEntityEntry = { primitiveIndex: number; bodyId: string; label: string }
    let entityEntries: PrimitiveEntityEntry[] = Array.isArray(object.userData?.primitiveEntityEntries)
      ? object.userData.primitiveEntityEntries as PrimitiveEntityEntry[]
      : []
    let primitiveIndexOffset = 0
    if (entityEntries.length === 0 && object.parent) {
      const parentEntries = Array.isArray(object.parent.userData?.primitiveEntityEntries)
        ? object.parent.userData.primitiveEntityEntries as PrimitiveEntityEntry[]
        : []
      if (parentEntries.length > 0) {
        let meshSiblingIndex = 0
        for (const sibling of object.parent.children) {
          if (sibling === object) {
            break
          }
          if (sibling instanceof THREE.Mesh) {
            meshSiblingIndex += 1
          }
        }
        entityEntries = parentEntries
        primitiveIndexOffset = meshSiblingIndex
      }
    }
    const brepBindingPlanContext = resolveImportedMeshBrepBindingPlanContext(object)

    for (let primitiveIndex = 0; primitiveIndex < primitiveCount; primitiveIndex += 1) {
      const primitiveBindingPlan = resolveImportedPrimitiveBrepBindingPlan(
        brepBindingPlanContext,
        primitiveIndex,
      )
      const primitiveExtras = primitiveBindingPlan?.primitiveExtras
      const primitiveId = buildPrimitiveId(meshId, primitiveIndex, primitiveExtras)
      const triangleCount = resolveTriangleCount(object, geometryGroups[primitiveIndex])
      const endFaceIndex = nextFaceStart + Math.max(0, triangleCount - 1)
      const primitiveVisibleBounds = primitiveBindingPlan?.brepRef
        ? computeImportedMeshPrimitiveLocalBounds(object, geometryGroups[primitiveIndex])
        : undefined
      primitiveIds.push(primitiveId)
      primitiveFaceRanges.push({
        primitiveId,
        startFaceIndex: nextFaceStart,
        endFaceIndex,
      })
      faceTriangleRanges.push([nextFaceStart, endFaceIndex + 1])

      const entityForPrimitive = entityEntries.find(
        (entry) => entry.primitiveIndex === primitiveIndex + primitiveIndexOffset,
      )
      const primitiveBrepRef = primitiveBindingPlan?.brepRef
      const primitiveBodyId = primitiveBindingPlan?.bodyId ?? entityForPrimitive?.bodyId
      primitiveBrepRefs.push(primitiveBrepRef)
      primitiveBodyIds.push(primitiveBodyId)
      primitiveDescriptors.push({
        primitiveId,
        primitiveIndex,
        bodyId: primitiveBodyId,
        brepRef: primitiveBrepRef,
        topologyRef: primitiveBindingPlan?.topologyRef,
        sourceLevel: primitiveBindingPlan?.sourceLevel,
        brepBodyOrdinal: primitiveBindingPlan?.brepBodyOrdinal,
        brepBodyKind: primitiveBindingPlan?.brepBodyKind,
        deferBodyBrepMatch: primitiveBindingPlan?.deferBodyBrepMatch,
        topologyFaceTriangleRanges: [
          ...(primitiveBindingPlan?.topologyFaceTriangleRanges ?? []),
          ...readImportedTopologyFaceTriangleRangeBindings(
            primitiveExtras?.__topo_face_ranges,
            primitiveBodyId,
          ),
        ],
      })
      if (primitiveVisibleBounds) {
        primitiveVisibleBoundsById[primitiveId] = primitiveVisibleBounds
      }

      primitiveCatalog[primitiveId] = {
        primitiveId,
        meshId,
        primitiveIndex,
        semanticKind: 'primitive',
        materialIndex: geometryGroups[primitiveIndex]?.materialIndex,
        brepRef: primitiveBrepRef,
        step: entityForPrimitive
          ? {
              entityId: entityForPrimitive.bodyId,
              entityName: entityForPrimitive.label,
              ...cloneMetadata(primitiveExtras?.step),
            }
          : cloneMetadata(primitiveExtras?.step),
        pmi: cloneMetadata(primitiveExtras?.pmi),
        nurbs: cloneMetadata(primitiveExtras?.nurbs),
      }
      nextFaceStart += triangleCount
    }

    object.userData.partAssetLocal = {
      meshId,
      primitiveIds,
      primitiveFaceRanges,
    }
    object.userData.faceTriangleRanges = faceTriangleRanges
    const meshBrepRef = brepBindingPlanContext.bindingPlan?.meshLevelBrepRef
      ?? resolveMeshBrepRef(primitiveBrepRefs)
    const meshBodyId = resolveMeshBodyId(primitiveBodyIds)
    const {
      bodyBrepBindings: meshBodyBrepBindings,
      bodyBrepBindingIdByPrimitiveId: meshBodyBrepBindingIdByPrimitiveId,
    } = buildImportedPartBodyBrepBindings({
      meshId,
      primitiveDescriptors,
    })
    meshCatalog[meshId] = {
      meshId,
      objectName: resolveImportedDisplayName(object, object.name || meshId),
      primitiveIds,
      materialNames: resolveMaterialNames(object),
      bodyId: meshBodyId,
      brepRef: meshBrepRef,
    }
    if (meshBrepRef) {
      brepBindingsByMeshId[meshId] = {
        meshId,
        bodyId: meshBodyId,
        primitiveId: primitiveIds.length === 1 ? primitiveIds[0] : undefined,
        brepRef: meshBrepRef,
      }
    }
    Object.entries(meshBodyBrepBindings).forEach(([bodyBindingId, binding]) => {
      bodyBrepBindings[bodyBindingId] = binding
      const visibleBounds = mergeImportedTopologyBounds(
        binding.primitiveIds.map((primitiveId) => primitiveVisibleBoundsById[primitiveId]),
      )
      if (visibleBounds) {
        visibleBoundsByBodyBindingId[bodyBindingId] = visibleBounds
      }
    })
    Object.entries(meshBodyBrepBindingIdByPrimitiveId).forEach(([primitiveId, bodyBindingId]) => {
      bodyBrepBindingIdByPrimitiveId[primitiveId] = bodyBindingId
    })
  }

  return {
    primitiveCatalog,
    meshCatalog,
    brepBindingsByMeshId,
    bodyBrepBindings,
    bodyBrepBindingIdByPrimitiveId,
    visibleBoundsByBodyBindingId,
  }
}

/** 收集作用域内对象声明的 body entries。 */
function collectBodyEntriesFromObjects(
  objects: readonly THREE.Object3D[],
): ImportedBodyEntry[] {
  const collections: ImportedBodyEntry[][] = []
  for (const object of objects) {
    const entries = Array.isArray(object.userData?.importedPartBodyEntries)
      ? object.userData.importedPartBodyEntries as ImportedBodyEntry[]
      : null
    if (entries && entries.length > 0) {
      collections.push(entries)
    }
  }
  return mergeBodyEntriesById(...collections)
}

/**
 * 将 occurrence 级 body 身份同步回当前 visible mesh 的 primitive 元数据。
 *
 * primitive 命中先从 mesh 本地 primitiveId 反查 bodyBindingId，因此必须与 topology source
 * 使用同一份 occurrence 身份；不能等到项目组合阶段再补写，否则源仓库已经以 definition ID 激活。
 *
 * @param objects 当前 topology source 实际拥有的对象。
 * @param source 已完成 occurrence 重绑定的拓扑源。
 */
function synchronizePrimitiveBodyBindingIds(
  objects: readonly THREE.Object3D[],
  source: ImportedTopologySource,
): void {
  for (const object of objects) {
    if (!(object instanceof THREE.Mesh)) {
      continue
    }
    const primitiveEntries = Array.isArray(object.userData?.primitiveEntityEntries)
      ? object.userData.primitiveEntityEntries as Array<Record<string, unknown>>
      : []
    const partAssetLocal = asRecord(object.userData?.partAssetLocal)
    const primitiveIds = Array.isArray(partAssetLocal?.primitiveIds)
      ? partAssetLocal.primitiveIds
      : []
    if (primitiveEntries.length === 0 || primitiveIds.length === 0) {
      continue
    }

    object.userData.primitiveEntityEntries = primitiveEntries.map((entry) => {
      const primitiveIndex = typeof entry.primitiveIndex === 'number'
        ? entry.primitiveIndex
        : -1
      const primitiveIdValue = primitiveIds[primitiveIndex]
      const primitiveId = typeof primitiveIdValue === 'string' ? primitiveIdValue : undefined
      const bodyBindingId = primitiveId
        ? source.bodyBrepBindingIdByPrimitiveId?.[primitiveId]
        : undefined
      return bodyBindingId ? { ...entry, bodyBindingId } : entry
    })
  }
}

/**
 * 从指定场景或 occurrence 范围构建导入拓扑源。
 *
 * @param root 场景或零件模板根。
 * @param metadata STEP、PMI 和单位元数据。
 * @param options 可选 occurrence 作用域。
 * @returns 统一导入拓扑源。
 */
export function buildImportedTopologySource(
  root: THREE.Object3D,
  metadata?: {
    step?: LoaderMetadata
    pmi?: LoaderMetadata
    unitContext?: ImportedModelUnitContext
  },
  options: PuppyCAD.ImportedTopologySourceBuildOptions = {},
): ImportedTopologySource {
  const objects = collectTopologySourceObjects(root, options)
  const meshes = objects.filter((object): object is THREE.Mesh => object instanceof THREE.Mesh)
  const {
    primitiveCatalog,
    meshCatalog,
    brepBindingsByMeshId,
    bodyBrepBindings,
    bodyBrepBindingIdByPrimitiveId,
    visibleBoundsByBodyBindingId,
  } = buildPartCatalogs(meshes)
  const bodyEntries = enrichBodyEntriesWithModelBodyIdentities(
    collectBodyEntriesFromObjects(objects),
    bodyBrepBindings,
  )
  const definitionSource: ImportedTopologySource = {
    bodyEntries,
    bodyPrimitiveIndicesById: buildBodyPrimitiveIndicesById(objects),
    primitiveCatalog,
    meshCatalog,
    brepBindingsByMeshId,
    bodyBrepBindings,
    bodyBrepBindingIdByPrimitiveId,
    visibleBoundsByBodyBindingId,
    step: metadata?.step ? { ...metadata.step } : undefined,
    pmi: metadata?.pmi ? { ...metadata.pmi } : undefined,
    unitContext: cloneImportedModelUnitContext(metadata?.unitContext),
  }
  const occurrenceNodeId = options.occurrenceNodeId?.trim()
  if (options.occurrenceNodeId !== undefined && !occurrenceNodeId) {
    throw new Error('拓扑源构建缺少有效 occurrenceNodeId')
  }
  const topologySource = occurrenceNodeId
    ? rebaseImportedTopologySourceOccurrence(definitionSource, occurrenceNodeId)
    : definitionSource

  // 只写回当前 occurrence 的 metadata carrier，不能遍历到兄弟或嵌套 occurrence。
  for (const object of objects) {
    const rawEntries = object.userData.importedPartBodyEntries
    if (!Array.isArray(rawEntries) || rawEntries.length === 0) {
      continue
    }
    const enrichedEntries = enrichBodyEntriesWithModelBodyIdentities(
      rawEntries as ImportedBodyEntry[],
      topologySource.bodyBrepBindings ?? {},
    )
    object.userData.importedPartBodyEntries = occurrenceNodeId
      ? enrichedEntries.map((entry) => ({
          ...entry,
          bodyBindingId: entry.bodyBindingId
            ? rebaseImportedBodyOccurrenceBindingId(entry.bodyBindingId, occurrenceNodeId)
            : entry.bodyBindingId,
        }))
      : enrichedEntries
  }
  synchronizePrimitiveBodyBindingIds(objects, topologySource)

  return topologySource
}
